// Copyright (c) StrangeBee 2023

import client from '@Client';
import { useEffect, useState, createContext, ReactElement, ReactNode } from 'react';
import { useQuery } from 'react-query';
import { Quotas, SchemaStatus, ServerStatus, StatusOutput, Config, ClusterNodes, QuotasOutput } from 'thehive-sdk';

interface StatusType {
    version: string;
    gitDescription: string;
    sso?: boolean;

    hasCortexConnector: boolean;
    hasMispConnector: boolean;

    hasCortex: boolean;
    hasMisp: boolean;

    mispServers: ServerStatus[];
    cortexServers: ServerStatus[];

    cortexStatus: 'success' | 'warning' | 'error';
    mispStatus: 'success' | 'warning' | 'error';

    hasOauth: boolean;

    capabilities: string[];
    authType: string[];
    licenseCapabilities: Record<string, any>;
    features: Set<string>;

    quotas: Quotas;
    quotasRaw: QuotasOutput;
    validLicense: boolean;

    config: Config;

    schemaStatus?: SchemaStatus[];
}

export enum ConnectorStatus {
    MISP = 'misp',
    CORTEX = 'cortex',
}

export enum AuthTypeStatus {
    OAUTH2 = 'oauth2',
}

export const SERVER_STATUS = {
    ERROR: 'ERROR',
    OK: 'OK',
    WARNING: 'WARNING',
};

const defaultObject: StatusType = {
    version: '',
    gitDescription: '',

    hasCortexConnector: false,
    hasMispConnector: false,

    hasCortex: false,
    hasMisp: false,

    mispServers: [],
    cortexServers: [],

    cortexStatus: 'success',
    mispStatus: 'success',

    hasOauth: false,

    config: {
        protectDownloadsWith: '',
        authType: [],
        capabilities: [],
        ssoAutoLogin: false,
        pollingDuration: 1000,
        freeTagDefaultColour: '#000000',
    },

    quotas: {
        'users.normal': { current: 0, quota: 0 },
        misp: { current: 0, quota: 0 },
        'cluster.nodes': { current: 0, quota: 0 },
        cortex: { current: 0, quota: 0 },
        organisations: { current: 0, quota: 0 },
    },
    quotasRaw: {},
    validLicense: true,

    capabilities: [],
    authType: [],
    licenseCapabilities: {},
    features: new Set(),
};

/**
 * @param {StatusOutput} data result query
 * @param {ConnectorStatus} key connector's name to check enabled
 * @returns {boolean}
 */
const hasConnector = (data: StatusOutput, key: ConnectorStatus = ConnectorStatus.CORTEX): boolean => {
    try {
        const { enabled = false } = data?.connectors?.[key] || {};

        return enabled;
    } catch (err) {
        return false;
    }
};

/**
 * @param {StatusOutput} data result query
 * @param {ConnectorStatus} key connector's name to check enabled
 * @returns {boolean}
 */
const hasServer = (data: StatusOutput, key: ConnectorStatus = ConnectorStatus.CORTEX): boolean => {
    try {
        const { enabled, servers } = data?.connectors?.[key] || {};

        return enabled && servers.some(({ status = SERVER_STATUS.ERROR }) => status === SERVER_STATUS.OK);
    } catch (err) {
        return false;
    }
};

/**
 * @param {StatusOutput} data result query
 * @param {ConnectorStatus} key connector's name to extract servers
 * @returns {ServerStatus[]} server's list
 */
const extractServer = (data: StatusOutput, key: ConnectorStatus = ConnectorStatus.CORTEX): ServerStatus[] => {
    const { servers = [] } = data?.connectors?.[key] || {};
    return servers;
};

/**
 * @param {StatusOutput} data result query
 * @param {ConnectorStatus} key connector's name to extract servers
 * @returns {ServerStatus[]} server's list
 */
const extractStatus = (
    data: StatusOutput,
    key: ConnectorStatus = ConnectorStatus.CORTEX,
): 'success' | 'warning' | 'error' => {
    const { status = 'OK' } = data?.connectors?.[key] || {};

    return status === 'OK' ? 'success' : status === 'NA' ? 'warning' : 'error';
};

/**
 * @param {StatusOutput} data result query
 * @param {ConnectorStatus} key connector's name to extract servers
 * @returns {ServerStatus[]} server's list
 */
const extractAuthType = (data: StatusOutput): string[] => {
    const authType = data?.config?.authType || [];
    return authType;
};

const extractCapabilities = (data: StatusOutput): string[] => {
    const capabilities = data?.config?.capabilities || [];
    return capabilities;
};

const extractQuotas = (data: StatusOutput): Quotas => {
    const quotas = Object.entries((data?.license?.quotas as QuotasOutput) || ({} as QuotasOutput)).reduce<Quotas>(
        (acc, [key, value]: [string, ClusterNodes]) => {
            const pathName = key.split('.').filter(Boolean);
            const [head, ...tail] = pathName;
            const treatedValue = { ...value, quota: value.quota < 0 ? Infinity : value.quota };

            treatedValue.valid = treatedValue.current < treatedValue.quota;

            let current = treatedValue;

            tail.forEach((pos) => {
                current = { ...(acc[pos] || {}), [pos]: treatedValue } as ClusterNodes;
            });

            return { ...acc, [head]: { ...(acc[head] || {}), ...current } };
        },
        {} as Quotas,
    );

    return quotas;
};

const extractLicenseCapabilities = (data: StatusOutput): Record<string, any> => {
    const capabilities = data?.license?.capabilities || [];

    return capabilities.reduce<Record<string, any>>((acc, name) => {
        const pathName = name.split('.').filter(Boolean);
        let current = {};

        pathName.forEach((pos) => {
            current = { ...(acc[pos] || {}), [pos]: true };
        });

        return { ...acc, [pathName[0]]: { ...(acc[pathName[0]] || {}), ...current } };
    }, {});
};

/**
 *
 * @param {StatusOutput} data
 */
const treatData = (data: StatusOutput): StatusType => {
    return {
        version: data.version,
        gitDescription: data.gitDescription,
        sso: data.sso,

        hasCortexConnector: hasConnector(data, ConnectorStatus.CORTEX),
        hasMispConnector: hasConnector(data, ConnectorStatus.MISP),

        hasCortex: hasServer(data, ConnectorStatus.CORTEX),
        hasMisp: hasServer(data, ConnectorStatus.MISP),

        mispServers: extractServer(data, ConnectorStatus.MISP),
        cortexServers: extractServer(data, ConnectorStatus.CORTEX),

        cortexStatus: extractStatus(data, ConnectorStatus.CORTEX),
        mispStatus: extractStatus(data, ConnectorStatus.MISP),

        hasOauth: extractAuthType(data).includes(AuthTypeStatus.OAUTH2),

        capabilities: extractCapabilities(data),
        authType: extractAuthType(data),
        licenseCapabilities: extractLicenseCapabilities(data),
        features: new Set(data.features),

        config: data.config,

        quotas: extractQuotas(data),
        quotasRaw: data?.license?.quotas || {},
        validLicense: data?.license?.isValid,

        schemaStatus: data.schemaStatus,
    };
};

export const useStatus = (): StatusType => {
    const [data, setData] = useState<StatusType>(defaultObject);
    const [lastHashData, setHashData] = useState<string>('');

    /**
     * get tierce application status
     */
    const query = useQuery<StatusOutput, string>(['getStatus'], () => client.StatusController.get('private'), {
        refetchInterval: 10000,
        enabled: true,
        refetchOnWindowFocus: false,
        onSuccess: (result) => {
            const dataTreated = treatData(result);
            const hashData = JSON.stringify(dataTreated);
            if (lastHashData !== hashData) {
                setHashData(hashData);
                setData(dataTreated);
            }
        },
    });

    useEffect(() => {
        query.refetch();
    }, []); // eslint-disable-line

    return data;
};

export const StatusContext = createContext<StatusType>(defaultObject);

interface StatusProviderProps {
    children?: ReactNode;
}

export default ({ children }: StatusProviderProps): ReactElement => {
    const status = useStatus();

    return <StatusContext.Provider value={status}>{children}</StatusContext.Provider>;
};
